<template>
  <b-card
    v-if="total > 0"
    no-body
    class="pending-banner mb-2"
  >
    <div class="pending-banner__header d-flex align-items-center flex-wrap">
      <div
        class="pending-banner__total"
        aria-hidden="true"
      >
        {{ total }}
      </div>
      <div class="flex-grow-1 mr-1">
        <h5 class="pending-banner__title mb-0">
          {{ total }} {{ total === 1 ? "solicitação aguarda" : "solicitações aguardam" }} sua ação
        </h5>
        <small class="pending-banner__text">
          Somente itens que dependem da sua resposta. Suas próprias solicitações ficam em “Minhas solicitações”.
        </small>
      </div>
      <b-button
        size="sm"
        variant="light"
        class="pending-banner__btn"
        :aria-expanded="open ? 'true' : 'false'"
        aria-controls="pending-banner-details"
        @click="toggle"
      >
        {{ open ? "Ocultar detalhes" : "Ver detalhes" }}
      </b-button>
    </div>

    <b-collapse
      id="pending-banner-details"
      v-model="open"
    >
      <div class="pending-banner__body">
        <section
          v-for="section in sections"
          :key="section.key"
          class="pending-banner__section"
        >
          <div class="pending-banner__section-title">
            {{ section.label }}
          </div>
          <div
            v-for="row in section.rows"
            :key="section.key + row.type"
            class="pending-banner__row d-flex align-items-center"
          >
            <span
              class="pending-banner__dot"
              :class="`pending-banner__dot--${row.priority.key}`"
              :title="`Prioridade ${row.priority.label}`"
            />
            <div class="flex-grow-1 min-width-0">
              <div class="pending-banner__row-title font-weight-bold">
                {{ row.title }}
              </div>
              <small class="pending-banner__row-subtitle text-muted">{{ row.subtitle }}</small>
            </div>
            <small class="pending-banner__priority text-muted d-none d-sm-inline">
              <strong>{{ row.priority.label }}</strong>
            </small>
            <b-badge
              pill
              variant="warning"
              class="mx-1"
            >
              {{ row.count }}
            </b-badge>
            <router-link
              :to="row.to"
              class="pending-banner__open"
            >
              Abrir →
            </router-link>
          </div>
        </section>
      </div>
    </b-collapse>
  </b-card>
</template>

<script>
import {
  BCard, BCollapse, BBadge, BButton,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import { SECTION_LABELS, buildRows } from '@/utils/pendingBanner'

const STORAGE_KEY = 'pendingBanner:open'
const REFRESH_AFTER_MS = 3 * 1000
const POLL_INTERVAL_MS = 10 * 1000

export default {
  name: 'PendingBanner',
  components: {
    BCard, BCollapse, BBadge, BButton,
  },
  data() {
    return { open: this.readOpen() }
  },
  computed: {
    ...mapGetters('pending', ['total', 'asPeer', 'asApprover']),
    sections() {
      return [
        { key: 'to_respond', label: SECTION_LABELS.to_respond, rows: buildRows(this.asPeer, 'peer') },
        { key: 'to_approve', label: SECTION_LABELS.to_approve, rows: buildRows(this.asApprover, 'approver') },
      ].filter(s => s.rows.length > 0)
    },
    userId() {
      return this.$store.state.user && this.$store.state.user.id
    },
  },
  watch: {
    userId: {
      immediate: true,
      handler(id) {
        if (id) this.$store.dispatch('pending/fetch')
      },
    },
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibility)
    this.timer = setInterval(this.refresh, POLL_INTERVAL_MS)
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibility)
    clearInterval(this.timer)
  },
  methods: {
    refresh() {
      if (document.visibilityState === 'visible' && this.userId) {
        this.$store.dispatch('pending/fetch', { maxAge: REFRESH_AFTER_MS })
      }
    },
    onVisibility() {
      this.refresh()
    },
    toggle() {
      this.open = !this.open
      try {
        window.localStorage.setItem(STORAGE_KEY, this.open ? '1' : '0')
        return true
      } catch (e) {
        return false
      }
    },
    readOpen() {
      try {
        return window.localStorage.getItem(STORAGE_KEY) === '1'
      } catch (e) {
        return false
      }
    },
  },
}
</script>

<style scoped>
.pending-banner {
  background: #fff5eb;
  border: 1px solid #ffdcae;
  border-radius: 12px;
  box-shadow: none;
}
.pending-banner__title {
  color: #7a3e00;
  font-weight: 700;
}
.pending-banner__text {
  color: #8a4b0f;
  font-size: 0.9rem;
}
.pending-banner__btn {
  background: #fff;
  border: 1px solid #ffdcae;
  color: #7a3e00;
  font-weight: 600;
}
.pending-banner__header {
  gap: 12px;
  padding: 1rem 1.25rem;
}
.pending-banner__total {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #ffdcae;
  color: #e8590c;
  font-weight: 700;
  font-size: 1.15rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pending-banner__body {
  padding: 0 1.25rem 1rem;
}
.pending-banner__section + .pending-banner__section {
  margin-top: 0.75rem;
}
.pending-banner__section-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a4b0f;
  padding: 0.5rem 0;
  border-bottom: 1px solid #ffdcae;
}
.pending-banner__row {
  gap: 4px;
  padding: 0.6rem 0;
  border-bottom: 1px solid rgba(255, 220, 174, 0.6);
}
.pending-banner__row:last-child {
  border-bottom: 0;
}
.pending-banner__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 10px;
  flex-shrink: 0;
}
.pending-banner__dot--urgent {
  background: #ea5455;
}
.pending-banner__dot--high {
  background: #ff9f43;
}
.pending-banner__dot--normal {
  background: #00cfe8;
}
.pending-banner__dot--low {
  background: #b9b9c3;
}
.pending-banner__priority {
  min-width: 52px;
  text-align: right;
  color: #000000!important;
}
.pending-banner__open {
  white-space: nowrap;
  font-weight: 600;
}
.pending-banner__row-subtitle {
  color: #000000!important;
}
.pending-banner__row-title {
  color: #000000!important;
}
.min-width-0 {
  min-width: 0;
}

.dark-layout .pending-banner {
  background: #3a2c1d;
  border-color: #6b4a22;
}
.dark-layout .pending-banner__title,
.dark-layout .pending-banner__text,
.dark-layout .pending-banner__section-title {
  color: #ffd9a8;
}
.dark-layout .pending-banner__section-title {
  border-bottom-color: rgba(255, 220, 174, 0.25);
}
.dark-layout .pending-banner__row-title {
  color: #fff !important;
}
.dark-layout .pending-banner__row-subtitle {
  color: #e6dccf !important;
}
.dark-layout .pending-banner__priority {
  color: #e6dccf !important;
}
.dark-layout .pending-banner__row {
  border-bottom-color: rgba(255, 220, 174, 0.12);
}
.dark-layout .pending-banner__btn {
  background: transparent;
  border-color: #6b4a22;
  color: #ffd9a8;
}
</style>
